import React, { useEffect, useRef, useState } from 'react';
import './CarrierManager.css';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  TextField,
  Typography,
  Grid,
  Divider,
  Avatar,
  IconButton,
  Alert,
  Paper,
  Stack
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  AddMoreRateRequest,
  GetQuoteDetail,
  LogTLCarrierReQuoteResponse,
  LogTLCarrierReScheduleResponse,
  LogTLCarrierResponse,
  UpdateTLShipment
} from '../../apis/Agency';
import CancelIcon from '@mui/icons-material/Cancel';
import { MetaTags } from 'react-meta-tags';
import { base_logo_url, base_url } from '../../apis/constants';
import { ErrorToast, successToast } from '../Toasts';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import TodayIcon from '@mui/icons-material/Today';
import { useIsMobile } from '../IsMobileHook/IsMobileHook';
import ShipmentDetailsComponent from '../../pages/ShipDetailsComponent';
import { ToastContainer } from 'react-toastify';

/* ── Design tokens ── */
const INPUT_HEIGHT = 38;
const LABEL_FONT = '0.78rem';
const INPUT_FONT = '0.85rem';
const BORDER_RADIUS = '8px';
const PANEL_HEIGHT = 'calc(106vh - 72px)';

/*
  Single CSS block injected once – forces ALL inputs
  (plain <input>, DatePicker, time input, MUI TextField)
  to share identical height / font / border / radius.
*/
const GLOBAL_INPUT_CSS = `
  .cm-field {
    width: 100%;
    height: ${INPUT_HEIGHT}px;
    font-size: ${INPUT_FONT};
    font-family: inherit;
    padding: 0 12px;
    border: 1.5px solid #d1d5db;
    border-radius: ${BORDER_RADIUS};
    background: #ffffff;
    color: #111827;
    box-sizing: border-box;
    outline: none;
    transition: border-color 0.18s, box-shadow 0.18s;
    -webkit-appearance: none;
    appearance: none;
  }
  .cm-field:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.12);
  }
  .cm-field:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
  .cm-field.cm-error { border-color: #d32f2f !important; }
  /* DatePicker wrapper fills its Grid cell */
  .cm-datepicker-wrap { width: 100%; }
  .cm-datepicker-wrap .react-datepicker-wrapper,
  .cm-datepicker-wrap .react-datepicker__input-container { width: 100%; }
  /* Time input – strip browser chrome */
  input[type="time"].cm-field { padding: 0 8px; cursor: pointer; }
  /* MUI Notes/Terms TextField */
  .cm-notes .MuiOutlinedInput-root {
    font-size: ${INPUT_FONT}; font-family: inherit; background: #ffffff;
    border-radius: ${BORDER_RADIUS};
  }
  .cm-notes .MuiOutlinedInput-notchedOutline {
    border: 1.5px solid #d1d5db !important;
    border-radius: ${BORDER_RADIUS} !important;
  }
  .cm-notes .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline { border-color: #9ca3af !important; }
  .cm-notes .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #2563eb !important;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.12);
  }
  /* Chat message input */
  .cm-chat-input .MuiOutlinedInput-root {
    font-size: ${INPUT_FONT}; font-family: inherit; background: #f9fafb;
    border-radius: ${BORDER_RADIUS};
  }
  .cm-chat-input .MuiOutlinedInput-notchedOutline {
    border: 1.5px solid #d1d5db !important;
    border-radius: ${BORDER_RADIUS} !important;
  }
  .cm-chat-input .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline { border-color: #9ca3af !important; }
  .cm-chat-input .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline { border-color: #2563eb !important; }
`;

/** Uniform label */
const FieldLabel = ({ error, children, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    style={{
      display: 'block',
      marginBottom: 4,
      fontSize: LABEL_FONT,
      fontWeight: 600,
      color: error ? '#d32f2f' : '#374151',
      letterSpacing: '0.02em'
    }}
  >
    {children}
  </label>
);

/** className helper – use on EVERY input / DatePicker / time */
const fieldClass = (hasError = false) =>
  hasError ? 'cm-field cm-error' : 'cm-field';

/** Legacy inline style – kept only for the time input width override */
const inputStyle = hasError => ({
  width: '100%',
  height: INPUT_HEIGHT,
  fontSize: INPUT_FONT,
  padding: '0 12px',
  border: `1.5px solid ${hasError ? '#d32f2f' : '#d1d5db'}`,
  borderRadius: BORDER_RADIUS,
  outline: 'none',
  background: '#fff',
  boxSizing: 'border-box',
  color: '#111827',
  transition: 'border-color 0.2s',
  fontFamily: 'inherit'
});

export default function CarrierManagerArea(props) {
  const isMobile = useIsMobile();

  const sessionId = new URLSearchParams(props?.location?.search).get('id');
  const quoteTypeQuery = props?.location?.pathname?.split('/')[2];

  const [userInput, setUserInput] = useState('');
  const [userInputArray, setUserInputArray] = useState([]);
  const [showInputValue, setShowInputValue] = useState(false);
  const [secondInputValue, setSecondIputValue] = useState('');
  const [shipmentsDetails, setShipmentsDetails] = useState({});
  const [charges, setCharges] = useState('');
  const [previousChargesRequote, setPreviousChargesRequote] = useState('');
  const [refPickNmb, setRefPickNmb] = useState('--select--');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [previousPickupDateReschedule, setPrevPickupDateReschedule] =
    useState('');
  const [
    previousEstimatedDeliveryDateReschedule,
    setPrevEstDeliveryDateReschedule
  ] = useState('');
  const [htmlApiRes, setHtmlApiRes] = useState('');
  const [shipperApiMsgs, setShipperApiMsgs] = useState('');
  const [isBookedCarrierSame, setIsBookedCarrierSame] = useState(false);
  const [carrierApiMsgs, setCarrierApiMsgs] = useState('');
  const [isCancelledCarrier, setIsCancelledCarrier] = useState(false);
  const [sessionIdForCarrier, setSessionIdForCarrier] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [chatLoader, setChatLoader] = useState(false);
  const [quoteLoader, setQuoteLoader] = useState(false);
  const [mainLoader, setMainLoader] = useState(false);
  const [expiryInMinutes, setExpiryInMinutes] = useState('');
  const [isBooked, setIsBooked] = useState(false);
  const [quoteApiError, setQuoteApiError] = useState('');
  const history = useHistory();
  const [timeExpired, setTimeExpired] = useState(false);
  const [showMoreRateBtn, setShowMoreRateBtn] = useState(false);
  const [localTimeState, setLocalTimeState] = useState('');
  const [addMoreRateDialog, setAddMoreRateDialog] = useState(false);
  const [quoteTitle, setQuoteTitle] = useState('');
  const dataFetchedRef = useRef(true);
  const [acknowledgeLoader, setAcknowledgeLoader] = useState(false);
  const [MCNumber, setMCNumber] = useState('');
  const [truckTailerNmb, setTruckTailerNmb] = useState('');
  const [CarrierName, setCarrierName] = useState('');

  function parseISODate(dateString) {
    const [datePart, timePart] = dateString.split('T');
    const [year, month, day] = datePart.split('-');
    const [hours, minutes, seconds] = timePart.split(':');
    const milliseconds = parseFloat(timePart.split('.')[1] || 0);
    return new Date(
      year,
      month - 1,
      day,
      hours,
      minutes,
      seconds,
      milliseconds
    );
  }

  const handleGetQuotedData = (apiRes, latestRates) => {
    setPickupDate(new Date(apiRes?.pickupDate));
    setPrevPickupDateReschedule(new Date(apiRes?.pickupDate));
    setDeliveryDate(new Date(apiRes?.expectedDeliveryDate));
    setPrevEstDeliveryDateReschedule(new Date(apiRes?.expectedDeliveryDate));
    if (apiRes?.expectedDeliveryDate) {
      const d = new Date(apiRes.expectedDeliveryDate);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      setDeliveryTime(`${hh}:${mm}`);
    }
    setCharges(latestRates);
    setPreviousChargesRequote(latestRates);
  };

  const handleSuccessResponse = (apiRes, stateStatus) => {
    const isBookedApi =
      (apiRes.data.data.shipmentInfo.shipment.shipmentStatus !== 'Tendered' &&
        !quoteTypeQuery) ||
      apiRes.data.data.shipmentInfo.shipment.shipmentStatus === 'InTransit' ||
      apiRes.data.data.shipmentInfo.shipment.shipmentStatus === 'Delivered'
        ? true
        : false;

    setTimeExpired(false);
    setIsBooked(isBookedApi);
    setHtmlApiRes(apiRes?.data?.data?.details);
    setShipperApiMsgs(apiRes?.data?.data?.details);

    const carrierData = apiRes?.data?.data?.details?.filter(
      items => items.actionByFlag.toLowerCase() === 'carrier'
    );
    const isCancelledCarrier = carrierData?.some(
      items => items.action === 'CANCELLED' || false
    );
    setIsCancelledCarrier(isCancelledCarrier);

    const isBookedCarrier = carrierData?.find(
      items =>
        items.carrierId === apiRes.data.data.shipmentInfo?.shipment?.carrierId
    );
    setIsBookedCarrierSame(isBookedCarrier);
    setCarrierApiMsgs(carrierData);

    if (
      (dataFetchedRef.current && quoteTypeQuery) ||
      stateStatus === 'maintainPrevState'
    ) {
      const lastMsgRate = carrierData[carrierData.length - 1].actionValue;
      handleGetQuotedData(
        apiRes?.data?.data?.shipmentInfo?.shipment,
        lastMsgRate
      );
      dataFetchedRef.current = false;
    }

    const expireyTime = apiRes?.data?.data?.details[0]?.date;
    setShipmentsDetails(apiRes?.data?.data?.shipmentInfo);
    setQuoteTitle(apiRes?.data?.data?.title);
    setChatLoader(false);
    setMainLoader(false);
    setExpiryInMinutes(apiRes?.data?.data?.expiryInMinutes);

    const utcTimeString = expireyTime + 'Z';
    const utcDate = new Date(utcTimeString);
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localTime = new Intl.DateTimeFormat('default', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      timeZone: userTimeZone
    }).format(utcDate);
    setLocalTimeState(localTime);
  };

  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const checkTimePassed = () => {
      if (localTimeState) {
        const currentTime = new Date();
        const startTime = new Date(localTimeState);
        const timeDifference = currentTime - startTime;
        if (timeDifference >= 60 * 60 * 1000) setTimeExpired(true);
      }
    };
    checkTimePassed();
    const intervalId = setInterval(checkTimePassed, 1 * 1000);
    return () => clearInterval(intervalId);
  }, [localTimeState]);

  useEffect(() => {
    const interval = setInterval(() => {
      CarrierQuoteReqHandler(sessionId);
    }, 5000);
    return () => clearInterval(interval);
  }, [sessionId]);

  const CarrierQuoteReqHandler = (sessionId, stateStatus) => {
    GetQuoteDetail(sessionId, stateStatus)
      .then(
        res => res.status === 200 && handleSuccessResponse(res, stateStatus)
      )
      .catch(error => {
        if (error.response?.status === 403) {
          console.error('403 - Forbidden:', error.response.data);
        } else {
          console.error('API error:', error);
          setMainLoader(false);
          setQuoteApiError(error?.message);
        }
      });
  };

  useEffect(() => {
    setMainLoader(true);
    setSessionIdForCarrier(sessionId);
    CarrierQuoteReqHandler(sessionId);
  }, []);

  const handleUserInput = input => setUserInput(input);

  const handleShowUserInputMsgs = type => {
    if (type === '2ndInput') {
      if (!charges) validationErrors['charges'] = 'Enter Charges';
      else if (!pickupDate)
        validationErrors['pickupDate'] = 'Select Pickup Date';
      else if (!deliveryDate)
        validationErrors['deliveryDate'] = 'Select Est. Delivery Date';
      else if (!deliveryTime)
        validationErrors['deliveryTime'] = 'Select Delivery Time';
      setValidationErrors(validationErrors);
    }
    let temp = [...userInputArray];
    temp.push({ msg: userInput });
    setUserInputArray(temp);
    getCurrentDateTime();
    setUserInput('');

    const logTlRequestDateHandler = (dateString, time = '') => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.toLocaleString('default', { month: 'numeric' });
      const day = date.getDate();
      const formattedDate = `${year}-${month}-${day}`;
      return time ? `${formattedDate} ${time}` : formattedDate;
    };

    if (type === '1stInput') {
      setChatLoader(true);
      const chatObj = {
        sessionId: sessionIdForCarrier,
        charges: 0,
        pickupNumber: '',
        pickupDate: logTlRequestDateHandler(pickupDate),
        estimatedDeliveryDate: logTlRequestDateHandler(
          deliveryDate,
          deliveryTime
        ),
        comments: userInput
      };
      LogTLCarrierResponse(chatObj)
        .then(res => {
          res.status === 200 && setShowMoreRateBtn(true);
          CarrierQuoteReqHandler(sessionIdForCarrier);
        })
        .catch(err => {
          setChatLoader(false);
          setShowMoreRateBtn(false);
        });
    }

    if (type === '2ndInput' && charges && pickupDate && deliveryDate) {
      setQuoteLoader(true);
      if (quoteTypeQuery === 'requote') {
        const requoteObj = {
          sessionId: sessionIdForCarrier ?? '',
          charges: charges
            ? Number(String(charges).replace(/[^0-9.]/g, ''))
            : 0,
          previousCharges: previousChargesRequote ?? 0,
          comments: secondInputValue ?? ''
        };
        LogTLCarrierReQuoteResponse(requoteObj)
          .then(res => {
            res.status === 200 && setQuoteLoader(false);
            setShowMoreRateBtn(true);
            CarrierQuoteReqHandler(sessionIdForCarrier, 'maintainPrevState');
          })
          .catch(err => {
            setQuoteLoader(false);
            setChatLoader(false);
            setShowMoreRateBtn(false);
          });
      }
      if (quoteTypeQuery === 'reschedule') {
        const rescheduleObj = {
          sessionId: sessionIdForCarrier,
          charges: charges
            ? Number(String(charges).replace(/[^0-9.]/g, ''))
            : 0,
          pickupDate: logTlRequestDateHandler(pickupDate),
          estimatedDeliveryDate: logTlRequestDateHandler(
            deliveryDate,
            deliveryTime
          ),
          comments: secondInputValue ?? '',
          previousPickupDate:
            logTlRequestDateHandler(previousPickupDateReschedule) ?? '',
          previousEstimatedDeliveryDate:
            logTlRequestDateHandler(previousEstimatedDeliveryDateReschedule) ??
            ''
        };
        LogTLCarrierReScheduleResponse(rescheduleObj)
          .then(res => {
            res.status === 200 && setQuoteLoader(false);
            setShowMoreRateBtn(true);
            CarrierQuoteReqHandler(sessionIdForCarrier, 'maintainPrevState');
          })
          .catch(err => {
            setQuoteLoader(false);
            setChatLoader(false);
            setShowMoreRateBtn(false);
          });
      } else if (!quoteTypeQuery) {
        const chatObj = {
          sessionId: sessionIdForCarrier,
          charges: charges
            ? Number(String(charges).replace(/[^0-9.]/g, ''))
            : 0,
          pickupNumber: '',
          pickupDate: logTlRequestDateHandler(pickupDate),
          estimatedDeliveryDate: logTlRequestDateHandler(
            deliveryDate,
            deliveryTime
          ),
          comments: secondInputValue
        };
        LogTLCarrierResponse(chatObj)
          .then(res => {
            res.status === 200 && setQuoteLoader(false);
            setShowMoreRateBtn(true);
            CarrierQuoteReqHandler(sessionIdForCarrier);
          })
          .catch(err => {
            setQuoteLoader(false);
            setShowMoreRateBtn(false);
          });
      }
    }
  };

  const handleKeyDown = (e, type) => {
    if (e.key === 'Enter') handleShowUserInputMsgs(type);
  };

  const bottomRef = useRef(null);
  const scrollToBottom = () =>
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  const prevLengthRef = useRef(0);

  useEffect(() => {
    if (shipperApiMsgs.length > prevLengthRef.current) scrollToBottom();
    prevLengthRef.current = shipperApiMsgs.length;
  }, [shipperApiMsgs]);

  const formatNumberWithCommas = value => {
    if (value === null || value === undefined || value === '') return '';
    const cleanedValue = String(value).replace(/,/g, '');
    if (!/^\d*\.?\d*$/.test(cleanedValue)) return value;
    const [integerPart, decimalPart] = cleanedValue.split('.');
    if (
      integerPart === '' ||
      (integerPart === '0' && !decimalPart && value.length === 1)
    )
      return '';
    const formattedInt = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimalPart !== undefined
      ? `${formattedInt}.${decimalPart}`
      : formattedInt;
  };

  const totalPieces =
    shipmentsDetails?.details?.length > 0 &&
    shipmentsDetails?.details.reduce(
      (sum, item) =>
        sum + Number(item.itemType === 'Pickup' ? item.pallets || 0 : 0),
      0
    );

  const pickups =
    shipmentsDetails?.details?.length > 0 &&
    shipmentsDetails?.details?.filter(item => item.itemType === 'Pickup');
  const deliveries =
    shipmentsDetails?.details?.length > 0 &&
    shipmentsDetails?.details?.filter(item => item.itemType === 'Delivery');

  const groupedPickupDetails =
    pickups &&
    pickups.reduce((acc, item) => {
      const key = `${item.address}-${item.city}-${item.state}-${item.zip}-${item.country}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

  const groupedDeliveryDetails =
    deliveries &&
    deliveries.reduce((acc, item) => {
      const key = `${item.address}-${item.city}-${item.state}-${item.zip}-${item.country}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

  const getCurrentDateTime = (() => {
    let initialTimestamp = null;
    return () => {
      if (!initialTimestamp) {
        initialTimestamp = new Date();
        let temp = [...userInputArray];
        temp.push({ timeStamp: initialTimestamp, msg: userInput });
        setUserInputArray(temp);
      }
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'July',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ];
      const monthName = months[initialTimestamp.getMonth()];
      const day = initialTimestamp.getDate();
      const year = initialTimestamp.getFullYear();
      const hours = initialTimestamp.getHours();
      const minutes = initialTimestamp.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const formattedTime = `${hours % 12 || 12}:${minutes < 10 ? '0' : ''}${minutes} ${ampm}`;
      let temp = [...userInputArray];
      temp.push({
        timeStamp: ` ${monthName} ${day}, ${year} ${formattedTime}`,
        msg: userInput
      });
      setUserInputArray(temp);
    };
  })();

  const handleDateTimeShow = (timeStamp, type) => {
    const dateTimeString = timeStamp + 'Z';
    const dateTime = new Date(dateTimeString);
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localTime = new Intl.DateTimeFormat('default', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      timeZone: userTimeZone
    }).format(dateTime);
    if (type === 'time') return localTime;
  };

  const handleAddMoreRates = () => {
    AddMoreRateRequest(sessionId)
      .then(res => {
        res.status === 200 &&
          window.open(`/carrier-manager?sessionId=${res.data.data}`, '_blank');
      })
      .catch(err => ErrorToast(err?.message));
  };

  const handleCloseAddMoreDialog = () => setAddMoreRateDialog(false);

  const handleAcknowledgeQuote = () => {
    const errors = {};
    if (!MCNumber) errors.MCNumberError = 'Enter MC #';
    else if (!truckTailerNmb)
      errors.truckTailerNmbError = 'Enter Truck/ Trailer #';
    else if (Object.keys(errors).length === 0) {
      setAcknowledgeLoader(true);
      const acknowledgeReqObj = {
        shipmentId: shipmentsDetails?.shipment?.id ?? 0,
        motorCarrierNumber: MCNumber ?? '',
        trailerNumber: truckTailerNmb ?? '',
        carrierName: CarrierName ?? ''
      };
      UpdateTLShipment(acknowledgeReqObj)
        .then(res => {
          res.status === 200 && setAcknowledgeLoader(false);
          successToast('Quote acknowledged successfully!');
        })
        .catch(err => {
          setAcknowledgeLoader(false);
          ErrorToast(err?.message);
        });
    }
    setValidationErrors(errors);
  };

  const handleDateChange = (userDate, type) => {
    if (type === 'deliveryDate') {
      setDeliveryDate(userDate);
      setValidationErrors({ ...validationErrors, deliveryDate: null });
    }
    if (type === 'pickupDate') {
      setPickupDate(userDate);
      setValidationErrors({ ...validationErrors, pickupDate: null });
    }
  };

  /* ─── panel height helper ──────────────────────────────── */
  const panelHeight = {
    xs: 'calc(100vh - 220px)',
    md: 'calc(100vh - 200px)',
    lg: 'calc(100vh - 160px)'
  };

  const panelSx = {
    height: panelHeight,
    overflowX: 'hidden',
    overflowY: 'auto',
    borderRadius: 2,
    p: 1.5,
    border: '1px solid',
    borderColor: 'grey.200',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    bgcolor: '#fff',
    transition: 'box-shadow 0.2s',
    '&:hover': { boxShadow: '0 4px 14px rgba(0,0,0,0.09)' }
  };

  /* ─── shipment stats bar ───────────────────────────────── */
  const StatCell = ({ label, value }) => (
    <Box>
      <Typography
        sx={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 500, mb: 0.3 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#111827' }}
      >
        {value || '—'}
      </Typography>
    </Box>
  );

  return (
    <Box
      className='Carrier_Manager_Parent'
      sx={{
        bgcolor: '#f8fafc',
        minHeight: '90vh',
        p: { xs: 1, md: 1, lg: 1 }
      }}
    >
      {/* ── Inject unified input styles once ── */}
      <style>{GLOBAL_INPUT_CSS}</style>
      <ToastContainer
        style={
          isMobile
            ? { width: '100%', display: 'flex', justifyContent: 'center' }
            : { width: 'auto' }
        }
        position='top-center'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* ── Confirm dialog ── */}
      <Dialog
        sx={{ '& .MuiDialog-paper': { minWidth: 360, borderRadius: 2 } }}
        open={addMoreRateDialog}
        onClose={handleCloseAddMoreDialog}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>
          Add More Rates?
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary'>
            A new rate session will open in a separate tab.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant='outlined'
            onClick={handleCloseAddMoreDialog}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            sx={{ borderRadius: 2, textTransform: 'none' }}
            onClick={() => {
              handleAddMoreRates();
              handleCloseAddMoreDialog();
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <MetaTags>
        <title>ShipTech Pro | Request for TL Quote</title>
      </MetaTags>

      {/* ── Success screen ── */}
      {showInputValue ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '80vh',
            bgcolor: '#f8fafc'
          }}
        >
          <Stack alignItems='center' spacing={2}>
            <CheckCircleOutlineIcon
              sx={{ color: '#16a34a', width: 100, height: 100 }}
            />
            <Typography variant='h5' fontWeight={600} color='#16a34a'>
              Quote sent to Shipping Manager!
            </Typography>
          </Stack>
        </Box>
      ) : /* ── Loading skeleton ── */
      mainLoader ? (
        <Box p={1}>
          <Skeleton sx={{ height: 72, borderRadius: 2 }} />
          <Skeleton sx={{ height: 700, mt: -15, borderRadius: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, mt: -12 }}>
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} sx={{ height: 40, flex: 1, borderRadius: 1 }} />
            ))}
          </Box>
          <Skeleton sx={{ height: 56, mt: 1, borderRadius: 2 }} />
        </Box>
      ) : (
        /* ── Main layout ── */
        !isBooked &&
        !isCancelledCarrier && (
          <Grid container spacing={1.5} sx={{ alignItems: 'flex-start' }}>
            {/* ════════════════════════════════════════════════
              LEFT PANEL – shipment details + quote form
              Both boxes together = same height as right panel
          ════════════════════════════════════════════════ */}
            <Grid
              item
              xs={12}
              lg={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                height: { lg: PANEL_HEIGHT }
              }}
            >
              {/* Shipment details panel – flex column: fixed header + scrollable body */}
              <Box
                sx={{
                  ...panelSx,
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  p: 0,
                  overflow: 'hidden'
                }}
              >
                {/* ── Panel header – same style as chat header on the right ── */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    bgcolor: '#fff',
                    borderRadius: '8px 8px 0 0',
                    flexShrink: 0
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: '#111827'
                    }}
                  >
                    Shipment Details
                  </Typography>
                  {shipmentsDetails?.quoteNumber && (
                    <Box
                      sx={{
                        px: 1.2,
                        py: 0.3,
                        bgcolor: '#EEF4FC',
                        borderRadius: 1.5,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#1d4ed8',
                        letterSpacing: '0.02em'
                      }}
                    >
                      # {shipmentsDetails.quoteNumber}
                    </Box>
                  )}
                </Box>

                {/* ── Scrollable body ── */}
                <Box
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    p: 1.5
                  }}
                >
                  {/* Stats grid */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 1.5,
                      bgcolor: '#EEF4FC',
                      borderRadius: 2,
                      p: 1.5,
                      mb: 1.5
                    }}
                  >
                    <StatCell
                      label='Total Net Weight (lbs)'
                      value={formatNumberWithCommas(
                        shipmentsDetails?.shipment?.totalNetWeight
                      )}
                    />
                    <StatCell
                      label='Total Gross Weight (lbs)'
                      value={formatNumberWithCommas(
                        shipmentsDetails?.shipment?.totalGrossWeight
                      )}
                    />
                    <StatCell label='Total # of Units' value={totalPieces} />
                    <StatCell
                      label='Pickup Mode'
                      value={shipmentsDetails?.quoteMode}
                    />
                    <StatCell
                      label='Equipment Type'
                      value={`${shipmentsDetails?.shipment?.equipmentType || ''}${shipmentsDetails?.shipment?.equipmentType === 'Reefer' ? ` (${shipmentsDetails?.temperature}${shipmentsDetails?.temperatureUnit})` : ''}`}
                    />
                    <StatCell
                      label='Service Type'
                      value={shipmentsDetails?.shipment?.serviceType}
                    />
                  </Box>

                  {shipmentsDetails?.details?.length > 0 && (
                    <ShipmentDetailsComponent
                      shipmentsDetails={shipmentsDetails?.details}
                      groupedPickupDetails={groupedPickupDetails}
                      groupedDeliveryDetails={groupedDeliveryDetails}
                      previousRowDetails={shipmentsDetails?.shipment}
                      quoteMode={shipmentsDetails?.quoteMode}
                    />
                  )}
                </Box>
              </Box>

              {/* ── Quote / Acknowledge form – pinned to bottom of left column ── */}
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: '#fff'
                }}
              >
                {/* ══ ACKNOWLEDGE mode ══ */}
                {quoteTypeQuery === 'acknowledge' && (
                  <Grid container spacing={2} alignItems='flex-end'>
                    <Grid item xs={12} sm={4}>
                      <FieldLabel
                        error={validationErrors.MCNumberError}
                        htmlFor='mc-number'
                      >
                        {validationErrors.MCNumberError || 'MC # *'}
                      </FieldLabel>
                      <input
                        id='mc-number'
                        placeholder='Enter MC #'
                        type='text'
                        className={fieldClass(!!validationErrors.MCNumberError)}
                        onChange={e => {
                          setValidationErrors({
                            ...validationErrors,
                            MCNumberError: null
                          });
                          setMCNumber(e.target.value);
                        }}
                        value={MCNumber}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FieldLabel
                        error={validationErrors.truckTailerNmbError}
                        htmlFor='truck-trailer'
                      >
                        {validationErrors.truckTailerNmbError ||
                          'Trailer / Truck # *'}
                      </FieldLabel>
                      <input
                        id='truck-trailer'
                        placeholder='Enter Trailer / Truck #'
                        type='text'
                        className={fieldClass(
                          !!validationErrors.truckTailerNmbError
                        )}
                        onChange={e => {
                          setValidationErrors({
                            ...validationErrors,
                            truckTailerNmbError: null
                          });
                          setTruckTailerNmb(e.target.value);
                        }}
                        value={truckTailerNmb}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FieldLabel
                        error={validationErrors.CarrierName}
                        htmlFor='carrier-name'
                      >
                        {validationErrors.CarrierName || 'Carrier Name'}
                      </FieldLabel>
                      <input
                        id='carrier-name'
                        placeholder='Enter Carrier Name'
                        type='text'
                        className={fieldClass(!!validationErrors.CarrierName)}
                        onChange={e => {
                          setValidationErrors({
                            ...validationErrors,
                            CarrierName: null
                          });
                          setCarrierName(e.target.value);
                        }}
                        value={CarrierName}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        disabled={acknowledgeLoader}
                        fullWidth
                        onClick={handleAcknowledgeQuote}
                        variant='contained'
                        color='success'
                        sx={{
                          height: INPUT_HEIGHT,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.84rem'
                        }}
                      >
                        Acknowledge
                        {acknowledgeLoader && (
                          <CircularProgress
                            color='inherit'
                            sx={{ ml: 1 }}
                            size={18}
                          />
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                )}

                {/* ══ QUOTE / REQUOTE / RESCHEDULE mode ══ */}
                {quoteTypeQuery !== 'acknowledge' && (
                  <Grid container spacing={1.5}>
                    {/* ── Row 1 : Freight Cost  |  Pickup Date ── */}
                    <Grid item xs={6}>
                      <FieldLabel
                        error={validationErrors.charges}
                        htmlFor='freight-cost'
                      >
                        {validationErrors.charges || 'All-In Freight Cost *'}
                      </FieldLabel>
                      <input
                        id='freight-cost'
                        disabled={
                          quoteTypeQuery === 'reschedule' ||
                          quoteTypeQuery === 'acknowledge'
                        }
                        placeholder='$'
                        type='text'
                        className={fieldClass(!!validationErrors.charges)}
                        value={
                          charges === '' ? '' : formatNumberWithCommas(charges)
                        }
                        onChange={e => {
                          const cleaned = e.target.value
                            .replace(/[^0-9.]/g, '')
                            .replace(/(\..*)\./g, '$1');
                          setValidationErrors(prev => ({
                            ...prev,
                            charges: null
                          }));
                          setCharges(cleaned);
                        }}
                        onKeyDown={e => {
                          if (
                            !(
                              (e.key >= '0' && e.key <= '9') ||
                              [
                                'Backspace',
                                'Delete',
                                'ArrowLeft',
                                'ArrowRight',
                                'Tab',
                                '.'
                              ].includes(e.key)
                            )
                          )
                            e.preventDefault();
                        }}
                        onPaste={e => {
                          if (
                            !/^[0-9.]+$/.test(e.clipboardData.getData('text'))
                          )
                            e.preventDefault();
                        }}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <FieldLabel
                        error={validationErrors.pickupDate}
                        htmlFor='pickup-date'
                      >
                        {validationErrors.pickupDate || 'Pickup Date *'}
                      </FieldLabel>
                      <DatePicker
                        disabled={
                          quoteTypeQuery === 'requote' ||
                          quoteTypeQuery === 'acknowledge'
                        }
                        wrapperClassName='cm-datepicker-wrap'
                        todayButton={<TodayIcon />}
                        placeholderText='mm/dd/yyyy'
                        className={fieldClass(!!validationErrors.pickupDate)}
                        selected={pickupDate}
                        onChange={e => handleDateChange(e, 'pickupDate')}
                        minDate={new Date()}
                      />
                    </Grid>

                    {/* ── Row 2 : Est. Delivery Date + Time  |  Submit button ── */}
                    <Grid item xs={8}>
                      <FieldLabel
                        error={
                          validationErrors.deliveryDate ||
                          validationErrors.deliveryTime
                        }
                        htmlFor='delivery-date'
                      >
                        {validationErrors.deliveryDate ||
                          validationErrors.deliveryTime ||
                          'Est. Delivery Date & Time *'}
                      </FieldLabel>
                      {/* Date + Time side-by-side inside this cell */}
                      <Box sx={{ display: 'flex', gap: '8px' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <DatePicker
                            disabled={
                              quoteTypeQuery === 'requote' ||
                              quoteTypeQuery === 'acknowledge'
                            }
                            wrapperClassName='cm-datepicker-wrap'
                            todayButton={<TodayIcon />}
                            placeholderText='mm/dd/yyyy'
                            className={fieldClass(
                              !!validationErrors.deliveryDate
                            )}
                            selected={
                              pickupDate > deliveryDate ? '' : deliveryDate
                            }
                            onChange={e => handleDateChange(e, 'deliveryDate')}
                            minDate={new Date(pickupDate)}
                          />
                        </Box>
                        <input
                          type='time'
                          id='deliveryTime'
                          disabled={
                            quoteTypeQuery === 'requote' ||
                            quoteTypeQuery === 'acknowledge'
                          }
                          value={deliveryTime}
                          onChange={e => setDeliveryTime(e.target.value)}
                          step={300}
                          className={fieldClass(
                            !!validationErrors.deliveryTime
                          )}
                          style={{ width: 110, flexShrink: 0 }}
                        />
                      </Box>
                    </Grid>

                    {/* Submit – vertically centred with Row 2 */}
                    <Grid
                      item
                      xs={4}
                      sx={{ display: 'flex', alignItems: 'flex-end' }}
                    >
                      <Button
                        disabled={quoteLoader}
                        fullWidth
                        onClick={() => handleShowUserInputMsgs('2ndInput')}
                        variant='contained'
                        color='primary'
                        sx={{
                          height: INPUT_HEIGHT,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.84rem',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {quoteTypeQuery || carrierApiMsgs.length > 0
                          ? 'Resubmit Quote'
                          : 'Submit Quote'}
                        {quoteLoader && (
                          <CircularProgress
                            color='inherit'
                            sx={{ ml: 1 }}
                            size={18}
                          />
                        )}
                      </Button>
                    </Grid>

                    {/* ── Row 3 : Notes / Terms – full width ── */}
                    <Grid item xs={12}>
                      <FieldLabel htmlFor='notes-terms'>
                        Notes / Terms
                      </FieldLabel>
                      <TextField
                        id='notes-terms'
                        className='cm-notes'
                        disabled={quoteTypeQuery === 'acknowledge'}
                        multiline
                        minRows={2}
                        maxRows={4}
                        fullWidth
                        size='small'
                        value={secondInputValue}
                        onChange={e => setSecondIputValue(e.target.value)}
                        placeholder='Enter any additional notes or terms…'
                        onKeyDown={e => handleKeyDown(e, '2ndInput')}
                      />
                    </Grid>
                  </Grid>
                )}
              </Paper>
            </Grid>

            {/* ════════════════════════════════════════════════
              RIGHT PANEL – chat
          ════════════════════════════════════════════════ */}
            <Grid
              item
              xs={12}
              lg={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: { lg: PANEL_HEIGHT }
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  bgcolor: '#f1f7fe',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  overflow: 'hidden'
                }}
              >
                {/* Chat header */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    bgcolor: '#fff',
                    borderRadius: '8px 8px 0 0'
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: '#111827'
                    }}
                  >
                    Chat with Shipper Representative
                  </Typography>
                </Box>

                {/* Messages */}
                <Box
                  className='Chat_Child'
                  sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}
                >
                  {shipperApiMsgs &&
                    shipperApiMsgs.map((carrierRes, idx) => {
                      const isShipper =
                        carrierRes.actionByFlag.toLowerCase() === 'shipper';
                      return (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            justifyContent: isShipper
                              ? 'flex-start'
                              : 'flex-end',
                            mb: 2
                          }}
                        >
                          <Box sx={{ maxWidth: '80%' }}>
                            {/* Sender info */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 0.5,
                                flexDirection: isShipper ? 'row' : 'row-reverse'
                              }}
                            >
                              <Avatar
                                src={
                                  base_logo_url +
                                  (isShipper
                                    ? shipperApiMsgs.find(
                                        i =>
                                          i.actionByFlag.toLowerCase() ===
                                          'shipper'
                                      )?.actionByImage
                                    : carrierApiMsgs[0]?.actionByImage)
                                }
                                sx={{
                                  width: 34,
                                  height: 34,
                                  bgcolor: isShipper ? '#eceff1' : '#e0f2f1'
                                }}
                              />
                              <Box
                                sx={{ textAlign: isShipper ? 'left' : 'right' }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    lineHeight: 1.2,
                                    color: '#111827'
                                  }}
                                >
                                  {isShipper
                                    ? shipperApiMsgs.find(
                                        i =>
                                          i.actionByFlag.toLowerCase() ===
                                          'shipper'
                                      )?.actionByName
                                    : carrierApiMsgs[0]?.actionByName}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '0.7rem',
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.03em'
                                  }}
                                >
                                  {isShipper
                                    ? shipperApiMsgs.find(
                                        i =>
                                          i.actionByFlag.toLowerCase() ===
                                          'shipper'
                                      )?.actionByDesignation
                                    : carrierApiMsgs[0]?.actionByDesignation}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Bubble */}
                            <Box
                              sx={{
                                bgcolor: isShipper ? '#ffffff' : '#d2f7f2',
                                borderRadius: isShipper
                                  ? '4px 12px 12px 12px'
                                  : '12px 4px 12px 12px',
                                p: '10px 14px',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                                fontSize: INPUT_FONT,
                                color: '#1f2937',
                                lineHeight: 1.6
                              }}
                            >
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: carrierRes.actionData
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: '0.68rem',
                                  color: '#9ca3af',
                                  mt: 0.5,
                                  textAlign: 'right'
                                }}
                              >
                                {handleDateTimeShow(carrierRes.date, 'time')}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  <div ref={bottomRef} />
                </Box>

                {/* Message input */}
                {!isBooked && (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.5,
                      bgcolor: '#fff',
                      borderTop: '1px solid',
                      borderColor: 'grey.200',
                      position: 'relative'
                    }}
                  >
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      size='small'
                      className='cm-chat-input'
                      placeholder='Type your message…'
                      value={userInput}
                      onChange={e => handleUserInput(e.target.value)}
                      onKeyDown={e => handleKeyDown(e, '1stInput')}
                      sx={{
                        '& .MuiOutlinedInput-root': { paddingRight: '48px' }
                      }}
                    />
                    {chatLoader ? (
                      <CircularProgress
                        size={22}
                        sx={{ position: 'absolute', bottom: 28, right: 22 }}
                      />
                    ) : (
                      <IconButton
                        disabled={userInput === ''}
                        onClick={() => handleShowUserInputMsgs('1stInput')}
                        size='small'
                        sx={{
                          position: 'absolute',
                          bottom: 24,
                          right: 18,
                          color: '#2563eb',
                          bgcolor: '#eff6ff',
                          width: 34,
                          height: 34,
                          borderRadius: '8px',
                          '&:hover': { bgcolor: '#dbeafe' },
                          '&.Mui-disabled': {
                            color: '#d1d5db',
                            bgcolor: 'transparent'
                          }
                        }}
                      >
                        <SendOutlinedIcon
                          fontSize='small'
                          sx={{ transform: 'rotate(-45deg)' }}
                        />
                      </IconButton>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )
      )}

      {/* ── Cancelled banner ── */}
      {isCancelledCarrier && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Alert
            severity='error'
            icon={<CancelIcon fontSize='inherit' />}
            sx={{
              width: '100%',
              maxWidth: 560,
              borderRadius: 2,
              fontWeight: 500,
              boxShadow: 2
            }}
          >
            This shipment has been cancelled.
          </Alert>
        </Box>
      )}

      {/* ── Booked / no longer available banner ── */}
      {isBooked && !isCancelledCarrier && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Alert
            severity={isBookedCarrierSame ? 'success' : 'warning'}
            icon={
              isBookedCarrierSame ? (
                <CheckCircleIcon fontSize='inherit' />
              ) : (
                <WarningAmberIcon fontSize='inherit' />
              )
            }
            sx={{
              width: '100%',
              maxWidth: 560,
              borderRadius: 2,
              fontWeight: 500,
              boxShadow: 2
            }}
          >
            {isBookedCarrierSame
              ? 'This shipment has been booked!'
              : 'This shipment is no longer available.'}
          </Alert>
        </Box>
      )}
    </Box>
  );
}
